/**
 * GiftPoolPost.js
 *
 * Shared component for rendering gift pool posts in both UserFeed and UserProfile.
 * Handles all post interactions: likes, comments, shares, donors display, etc.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SERVER_URL } from '../config';
import PoolDisplay from './PoolDisplay';
import { formatDateTime } from '../utils/timeUtils';
import BeneficiaryInfoBanner from './BeneficiaryInfoBanner';
import Avatar from './Avatar';
import GiftDrawer from './GiftDrawer';

// Linkify URLs only in admin comments; we keep regular user comments as plain
// text to avoid giving non-admins an easy channel for malicious links.
const URL_REGEX = /\bhttps?:\/\/[^\s<>()[\]{}"']+/gi;
function renderAdminCommentContent(content) {
  if (!content) return content;
  const parts = [];
  let lastIndex = 0;
  let match;
  URL_REGEX.lastIndex = 0;
  while ((match = URL_REGEX.exec(content)) !== null) {
    if (match.index > lastIndex) parts.push(content.slice(lastIndex, match.index));
    let url = match[0];
    let trailing = '';
    while (/[.,;:!?)]$/.test(url)) {
      trailing = url.slice(-1) + trailing;
      url = url.slice(0, -1);
    }
    parts.push(
      <a
        key={`${match.index}-${url}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-indigo-600 hover:text-indigo-700 underline break-all"
      >
        {url}
      </a>
    );
    if (trailing) parts.push(trailing);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < content.length) parts.push(content.slice(lastIndex));
  return parts;
}

function EngagementSection({ expanded, onClose, title, items, emptyMessage, bg, renderItem }) {
  if (!expanded) return null;
  return (
    <div className={`px-3 sm:px-5 py-4 border-t border-gray-100${bg ? ` ${bg}` : ''}`}>
      <div className="flex justify-between items-center mb-3">
        <div className="font-semibold text-gray-900 flex items-center gap-1.5">{title}</div>
        <button onClick={onClose} className="text-sm text-gray-600 hover:text-gray-900">✕ Close</button>
      </div>
      {items && items.length > 0 ? (
        <div className="space-y-2">{items.map(renderItem)}</div>
      ) : (
        <p className="text-sm text-gray-500 text-center py-4">{emptyMessage}</p>
      )}
    </div>
  );
}

function GiftPoolPost({
  post,
  user,
  isAuthenticated,
  // State
  likedPosts,
  sharedPosts,
  vouchedPosts,
  commentedPosts,
  expandedDonors,
  expandedLikes,
  expandedComments,
  expandedDescriptions,
  expandedVouches,
  donors,
  likers,
  comments,
  newComment,
  vouchers,
  // Handlers
  onLike,
  onShare,
  onVouch,
  onToggleDonors,
  onToggleLikes,
  onToggleComments,
  onToggleDescription,
  onToggleVouches,
  onAddComment,
  onCommentChange,
  // Reply props
  replyingTo,
  replyText,
  onReplyClick,
  onReplyTextChange,
  onAddReply,
  // Optional props for UserFeed-specific features
  selectedCategory,
  onCategoryClick,
  onUrgentClick,
  showUrgentOnly,
  followingStatusLoaded,
  followingUsers,
  followLoading,
  onFollowToggle,
  // Optional for highlighting (UserProfile)
  highlightedPostId,
  // Comment actions
  onDeleteComment,
  onEditComment,
  // Sharers
  expandedShares,
  sharers,
  onToggleShares,
  // Optional edit handler
  onEditPost,
  // Context flag
  isProfilePage,
}) {
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [commentLikes, setCommentLikes] = useState({}); // { [commentId]: { liked, count } }
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [flagModal, setFlagModal] = useState(false);
  const [flagReason, setFlagReason] = useState('');
  const [flagDetails, setFlagDetails] = useState('');
  const [flagSubmitting, setFlagSubmitting] = useState(false);
  const [flagDone, setFlagDone] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const handleVouchClick = (e, post) => {
    e.preventDefault();
    onVouch(post);
  };

  // Initialize comment likes from loaded comments data
  const postComments = comments[post.id];
  useEffect(() => {
    if (!postComments || !user) return;
    const likes = {};
    for (const c of postComments) {
      likes[c.id] = { liked: (c.liked_by || []).includes(user.email), count: c.like_count || 0 };
      if (c.replies) {
        for (const r of c.replies) {
          likes[r.id] = { liked: (r.liked_by || []).includes(user.email), count: r.like_count || 0 };
        }
      }
    }
    setCommentLikes(likes);
  }, [postComments, user]);

  const handleCommentLike = async (commentId) => {
    if (!user) return;
    // Optimistic update
    setCommentLikes(prev => {
      const current = prev[commentId] || { liked: false, count: 0 };
      return { ...prev, [commentId]: { liked: !current.liked, count: current.liked ? current.count - 1 : current.count + 1 } };
    });
    try {
      const res = await fetch(`${SERVER_URL}/comments/${commentId}/like?user_email=${encodeURIComponent(user.email)}`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setCommentLikes(prev => ({ ...prev, [commentId]: { liked: data.liked, count: data.like_count } }));
      }
    } catch (err) {
      console.error('Error toggling comment like:', err);
    }
  };

  const isHighlighted = highlightedPostId === post.id;
  const [postImages, setPostImages] = useState(
    post.image_url ? [{ image_url: post.image_url, display_order: 0 }] : []
  );
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [, setTick] = useState(0);

  // Re-render every 60s so relative timestamps stay fresh
  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(timer);
  }, []);

  // Fetch post images
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await fetch(`${SERVER_URL}/posts/${post.id}/images`);
        if (response.ok) {
          const data = await response.json();
          if (data.images && data.images.length > 0) {
            setPostImages(data.images);
          } else if (post.image_url) {
            // Fallback to single image if no images in post_images table
            setPostImages([{ image_url: post.image_url, display_order: 0 }]);
          }
        } else if (post.image_url) {
          // Fallback to single image if fetch fails
          setPostImages([{ image_url: post.image_url, display_order: 0 }]);
        }
      } catch (error) {
        console.error('Error fetching post images:', error);
        // Fallback to single image on error
        if (post.image_url) {
          setPostImages([{ image_url: post.image_url, display_order: 0 }]);
        }
      }
    };

    fetchImages();
  }, [post.id, post.image_url]);

  const nextImage = () => {
    setCurrentImageIndex(prev => (prev + 1) % postImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex(prev => (prev - 1 + postImages.length) % postImages.length);
  };

  const beneficiaryFirstName = ((post.beneficiary_name === 'Self' ? post.user_name : post.beneficiary_name) || '').split(' ')[0];
  const isPostActive = post.status !== 'closed' && (post.acceptance_status === 'accepted' || !post.acceptance_status);
  const giftingPaused = isPostActive && (post.is_example || !post.beneficiary_has_paypal);
  const pausedMessage = post.is_example
    ? 'This is an example post — gifting is disabled'
    : `Gifts paused - ${beneficiaryFirstName} hasn't connected PayPal yet`;

  return (
    <div
      id={`post-${post.id}`}
      className={`bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden ${
        isHighlighted ? 'ring-2 ring-indigo-400' : ''
      }`}
    >
      {/* Repost banner */}
      {post.is_shared && (
        <div className="px-3 sm:px-5 pt-3 pb-1 flex items-center gap-1.5 text-xs text-gray-500 border-b border-gray-100">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 flex-shrink-0">
            <path fillRule="evenodd" d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0V5.36l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389A5.5 5.5 0 0 1 13.89 6.11l.311.31h-2.432a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.219Z" clipRule="evenodd" />
          </svg>
          <span>Reposted by</span>
          {post.sharer_id ? (
            <Link to={`/profile/${post.sharer_id}`} className="font-medium text-gray-700 hover:underline">
              {post.sharer_name || 'someone'}
            </Link>
          ) : (
            <span className="font-medium text-gray-700">{post.sharer_name || 'someone'}</span>
          )}
          {post.shared_at && (
            <span className="text-gray-400">· {formatDateTime(post.shared_at)}</span>
          )}
        </div>
      )}

      {/* Header - Identity */}
      <div className="p-3 sm:p-5">
        <div className="flex items-start gap-3 mb-4">
          <Link
            to={`/profile/${post.user_id}`}
            className="flex-shrink-0"
          >
            <Avatar
              name={post.user_name}
              email={post.user_email}
              profilePhotoUrl={post.user_profile_photo_url}
              size="w-10 h-10"
              textSize="text-base"
            />
          </Link>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className="text-gray-900 leading-snug">
                {post.beneficiary_name && post.beneficiary_name !== 'Self' && post.beneficiary_id !== post.user_id ? (
                  <>
                    <Link to={`/profile/${post.user_id}`} className="font-semibold hover:underline">
                      {post.user_name}
                    </Link>
                    <span className="text-gray-600"> has started a gift pool for </span>
                    {post.beneficiary_id ? (
                      <Link
                        to={`/profile/${post.beneficiary_id}`}
                        className="font-semibold hover:underline"
                      >
                        {post.beneficiary_name}
                      </Link>
                    ) : (
                      <span className="font-semibold">{post.beneficiary_name}</span>
                    )}
                  </>
                ) : (
                  <Link to={`/profile/${post.user_id}`} className="font-semibold hover:underline">
                    {post.user_name}
                  </Link>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {formatDateTime(post.created_at)}
              </span>
              {isAuthenticated && user && user.id === post.user_id && (
                <>
                  <span className="text-xs text-gray-400 hidden sm:inline">·</span>
                  <div className="relative group/vis">
                    <span className="text-gray-400 cursor-default flex items-center">
                      {post.visibility === 'followers' ? (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-3.5 h-3.5">
                          <circle cx="7.5" cy="7" r="2.5"/>
                          <circle cx="13" cy="7" r="2.5"/>
                          <path d="M2.5 16.5a5 5 0 0 1 10 0" strokeLinecap="round"/>
                          <path d="M11 15a5 5 0 0 1 7 1.5" strokeLinecap="round"/>
                        </svg>
                      ) : post.visibility === 'private' ? (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                          <path fillRule="evenodd" d="M10 1a4.5 4.5 0 0 0-4.5 4.5V9H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2h-.5V5.5A4.5 4.5 0 0 0 10 1Zm3 8V5.5a3 3 0 1 0-6 0V9h6Z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-3.5 h-3.5">
                          <circle cx="10" cy="10" r="7.5"/>
                          <ellipse cx="10" cy="10" rx="3.5" ry="7.5"/>
                          <line x1="2.5" y1="10" x2="17.5" y2="10"/>
                        </svg>
                      )}
                    </span>
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/vis:block z-20 pointer-events-none">
                      <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg">
                        {post.visibility === 'followers'
                          ? 'Visible to your followers only'
                          : post.visibility === 'private'
                          ? 'Only visible to people with the link'
                          : 'Visible to everyone on Gifted'}
                      </div>
                    </div>
                  </div>
                </>
              )}
              <span className="text-xs text-gray-400 hidden sm:inline">·</span>
              {onCategoryClick ? (
                <button
                  onClick={() => onCategoryClick(post.category)}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {post.category}
                </button>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 whitespace-nowrap">
                  {post.category}
                </span>
              )}
              {post.is_time_sensitive && (
                <>
                  <span className="text-xs text-gray-400 hidden sm:inline">·</span>
                  {onUrgentClick ? (
                    <button
                      onClick={onUrgentClick}
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${showUrgentOnly ? 'bg-red-100 text-red-700' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}
                    >
                      Urgent
                    </button>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-600 whitespace-nowrap">
                      Urgent
                    </span>
                  )}
                </>
              )}
              {post.acceptance_status === 'pending' && (
                <>
                  <span className="text-xs text-gray-400 hidden sm:inline">·</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-indigo-100 text-indigo-700 border border-indigo-300 whitespace-nowrap">
                    <span className="hidden sm:inline">⏳ PENDING ACCEPTANCE</span>
                    <span className="sm:hidden">⏳ PENDING</span>
                  </span>
                </>
              )}
              {post.acceptance_status === 'declined' && (
                <>
                  <span className="text-xs text-gray-400 hidden sm:inline">·</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-300 whitespace-nowrap">
                    ❌ DECLINED
                  </span>
                </>
              )}
              {post.status === 'closed' && (
                <>
                  <span className="text-xs text-gray-400 hidden sm:inline">·</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-600 border border-gray-300 whitespace-nowrap">
                    🔒 CLOSED
                  </span>
                </>
              )}
            </div>
          </div>
          {/* Edit button - only for post owner */}
          {isAuthenticated && user && user.id === post.user_id && onEditPost && (
            <button
              onClick={e => {
                e.stopPropagation();
                onEditPost(post);
              }}
              className="text-sm text-indigo-600 hover:text-indigo-700 transition-colors font-medium"
              title="Edit post"
            >
              ✏️ Edit
            </button>
          )}
          {/* Follow button - only in UserFeed */}
          {onFollowToggle &&
            isAuthenticated &&
            user &&
            user.id !== post.user_id &&
            followingStatusLoaded &&
            !followingUsers.has(post.user_id) && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  onFollowToggle(post.user_id);
                }}
                disabled={followLoading[post.user_id]}
                className="text-sm font-bold text-gray-700 hover:text-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {followLoading[post.user_id] ? '...' : 'Follow'}
              </button>
            )}
        </div>

        {/* Title */}
        <h3 className="text-lg font-display font-semibold text-gray-900 mb-3 line-clamp-2 leading-snug">{post.title}</h3>

        {/* Image Gallery */}
        {postImages.length > 0 && (
          <div className="mb-4 relative aspect-[4/3] bg-white rounded-lg overflow-hidden">
            <img
              src={postImages[currentImageIndex].image_url}
              alt={`${post.title} - ${currentImageIndex + 1}`}
              className="absolute inset-0 w-full h-full object-contain"
              onError={e => {
                e.target.parentElement.style.display = 'none';
              }}
            />

            {/* Image counter and navigation */}
            {postImages.length > 1 && (
              <>
                {/* Image counter */}
                <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded-full">
                  {currentImageIndex + 1} / {postImages.length}
                </div>

                {/* Navigation buttons */}
                <button
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white rounded-full w-8 h-8 flex items-center justify-center transition-all"
                  aria-label="Previous image"
                >
                  ‹
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white rounded-full w-8 h-8 flex items-center justify-center transition-all"
                  aria-label="Next image"
                >
                  ›
                </button>

                {/* Dot indicators */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {postImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentImageIndex
                          ? 'bg-white w-6'
                          : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                      }`}
                      aria-label={`Go to image ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Description with Read More */}
        {post.description && (
          <div className="mb-4">
            <p
              className={`text-gray-600 text-sm leading-relaxed whitespace-pre-wrap ${expandedDescriptions.has(post.id) ? '' : 'line-clamp-[10]'}`}
            >
              {post.description}
            </p>
            {post.description.length > 500 && onToggleDescription && (
              <button
                onClick={() => onToggleDescription(post.id)}
                className="text-indigo-600 hover:text-indigo-700 text-sm font-medium mt-1"
              >
                {expandedDescriptions.has(post.id) ? 'Show less' : 'Read more'}
              </button>
            )}
          </div>
        )}


        {/* Engagement Actions */}
        {giftingPaused && (
          <p className="text-xs text-gray-400 mb-2">{pausedMessage}</p>
        )}

        <div className="flex items-center gap-2 text-sm mb-4 w-full">

          {/* Like pill: icon+label = like action, count = expand likers */}
          <div className="relative group">
            <div className="flex items-center rounded-full text-sm font-medium bg-gray-100 text-gray-600">
              <button
                onClick={e => { e.preventDefault(); onLike(post); }}
                className="flex items-center pl-3 pr-1.5 py-1.5 hover:opacity-75 transition-opacity"
              >
                {likedPosts.has(post.id) ? (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#ef4444" className="w-4 h-4">
                    <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 0 1-.383-.218 25.18 25.18 0 0 1-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0 1 12 5.052 5.5 5.5 0 0 1 16.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 0 1-4.244 3.17 15.247 15.247 0 0 1-.383.219l-.022.012-.007.004-.003.001a.752.752 0 0 1-.704 0l-.003-.001Z" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                    <path d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z" />
                  </svg>
                )}
              </button>
              {post.like_count > 0 ? (
                <button
                  onClick={() => onToggleLikes(post.id)}
                  className="pr-3 py-1.5 hover:opacity-75 transition-opacity"
                >
                  {post.like_count}
                </button>
              ) : (
                <span className="pr-3" />
              )}
            </div>
            <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-20 pointer-events-none">
              <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg">
                Show some love
              </div>
            </div>
          </div>

          {/* Vouch pill: icon+label = vouch action, count = expand vouchers */}
          {onVouch && !(isAuthenticated && user && (user.id === post.user_id || user.email === post.beneficiary_email)) && (
            <div className="relative group">
              <div className={`flex items-center rounded-full text-sm font-medium transition-colors ${
                'bg-gray-100 text-gray-600'
              }`}>
                <button
                  onClick={e => { handleVouchClick(e, post); }}
                  className={`flex items-center py-1.5 hover:opacity-75 transition-opacity ${post.vouch_count > 0 ? 'pl-3 pr-1.5' : 'px-3'}`}
                >
                  {vouchedPosts && vouchedPosts.has(post.id) ? (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#3b82f6" className="w-4 h-4">
                      <path fillRule="evenodd" d="M12.516 2.17a.75.75 0 0 0-1.032 0 11.209 11.209 0 0 1-7.877 3.08.75.75 0 0 0-.722.515A12.74 12.74 0 0 0 2.25 9.75c0 5.942 4.064 10.933 9.563 12.348a.749.749 0 0 0 .374 0c5.499-1.415 9.563-6.406 9.563-12.348 0-1.39-.223-2.73-.635-3.985a.75.75 0 0 0-.722-.516l-.143.001c-2.996 0-5.717-1.17-7.734-3.08Zm3.094 8.016a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
                    </svg>
                  )}
                </button>
                {post.vouch_count > 0 && (
                  <button
                    onClick={() => onToggleVouches && onToggleVouches(post.id)}
                    className="pr-3 py-1.5 hover:opacity-75 transition-opacity"
                  >
                    {post.vouch_count}
                  </button>
                )}
              </div>
              <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-20 pointer-events-none">
                <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg">
                  Vouch that you personally know this person and trust their request
                </div>
              </div>
            </div>
          )}

          {/* Comment pill */}
          <button
            onClick={() => onToggleComments(post.id)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
          >
            {isAuthenticated && user && commentedPosts?.has(post.id) ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#eab308" className="w-4 h-4">
                <path fillRule="evenodd" d="M4.848 2.771A49.144 49.144 0 0 1 12 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 0 1-3.476.383.39.39 0 0 0-.297.17l-2.755 4.133a.75.75 0 0 1-1.248 0l-2.755-4.133a.39.39 0 0 0-.297-.17 48.9 48.9 0 0 1-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97Z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                <path d="M2.25 12.76c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 0 1 1.037-.443 48.282 48.282 0 0 0 5.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
              </svg>
            )}
            {post.comment_count > 0 && <span>{post.comment_count}</span>}
          </button>

          {/* Repost pill */}
          {onShare && (
            <div className="relative group">
              <div className="flex items-center rounded-full text-sm font-medium bg-gray-100 text-gray-600">
                <button
                  onClick={e => { e.preventDefault(); onShare(post); }}
                  className="flex items-center pl-3 pr-1.5 py-1.5 hover:opacity-75 transition-opacity"
                >
                  {sharedPosts && sharedPosts.has(post.id) ? (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#22c55e" className="w-4 h-4">
                      <path fillRule="evenodd" d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0V5.36l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389A5.5 5.5 0 0 1 13.89 6.11l.311.31h-2.432a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.219Z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#6b7280" className="w-4 h-4">
                      <path fillRule="evenodd" d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0V5.36l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389A5.5 5.5 0 0 1 13.89 6.11l.311.31h-2.432a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.219Z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
                {post.share_count > 0 ? (
                  <button
                    onClick={() => onToggleShares && onToggleShares(post.id)}
                    className="pr-3 py-1.5 hover:opacity-75 transition-opacity"
                  >
                    {post.share_count}
                  </button>
                ) : (
                  <span className="pr-3" />
                )}
              </div>
              <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-20 pointer-events-none">
                <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg">
                  {sharedPosts && sharedPosts.has(post.id) ? 'Remove repost' : 'Repost to your network'}
                </div>
              </div>
            </div>
          )}

          {/* Gift pill - primary CTA */}
          {isPostActive && (
            giftingPaused ? (
              <div className="relative group cursor-not-allowed">
                <button
                  disabled
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-gray-200 text-gray-400 cursor-not-allowed pointer-events-none"
                >
                  💝 Gift {beneficiaryFirstName}
                </button>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 text-xs text-white bg-gray-700 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-100 pointer-events-none">
                  {pausedMessage}
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowGiftModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-indigo-500 text-white hover:bg-indigo-600 transition-colors"
              >
                💝 Gift {beneficiaryFirstName}
              </button>
            )
          )}

          {/* Share + Flag buttons - far right */}
          <div className="ml-auto flex items-center gap-1">
            {isAuthenticated && user && user.id !== post.user_id && (
              <button
                onClick={() => { setFlagModal(true); setFlagDone(false); setFlagReason(''); setFlagDetails(''); }}
                className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 hover:text-red-500 transition-colors"
                aria-label="Flag"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                  <path d="M3 3v1.5M3 21v-6m0 0 2.77-.693a9 9 0 0 1 6.208.682l.108.054a9 9 0 0 0 6.086.71l3.114-.732a48.524 48.524 0 0 1-.005-10.499l-3.11.732a9 9 0 0 1-6.085-.711l-.108-.054a9 9 0 0 0-6.208-.682L3 4.5M3 15V4.5" />
                </svg>
              </button>
            )}
            <button
              onClick={() => setShowShareModal(true)}
              className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
              aria-label="Share"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                <polyline points="15 17 20 12 15 7" />
                <path d="M20 12H9a5 5 0 0 0-5 5v1" />
              </svg>
            </button>
          </div>

        </div>

        {/* Gift drawer */}
        {showGiftModal && (
          <GiftDrawer
            onSuccess={() => {}}
            onClose={() => setShowGiftModal(false)}
            initialRecipient={post.beneficiary_name === 'Self' ? post.user_name : post.beneficiary_name}
            initialEmail={post.beneficiary_email}
            initialPostId={String(post.id)}
            initialMessage={`Supporting gift pool: ${post.title}`}
          />
        )}

        {/* Flag modal */}
        {flagModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onClick={() => setFlagModal(false)}>
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4" onClick={e => e.stopPropagation()}>
              {flagDone ? (
                <div className="text-center space-y-2">
                  <div className="text-2xl">✅</div>
                  <p className="font-semibold text-gray-900">Thanks for letting us know.</p>
                  <p className="text-sm text-gray-500">We'll review this post.</p>
                  <button onClick={() => setFlagModal(false)} className="mt-2 text-sm text-indigo-600 hover:underline">Close</button>
                </div>
              ) : (
                <>
                  <h3 className="font-semibold text-gray-900 mb-4">Flag a concern</h3>
                  <div className="space-y-3">
                    <select
                      value={flagReason}
                      onChange={e => setFlagReason(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                    >
                      <option value="">Select a reason…</option>
                      <option value="Fraudulent/Fake">Fraudulent or fake</option>
                      <option value="Scam/Spam">Scam or spam</option>
                      <option value="Inappropriate">Inappropriate content</option>
                      <option value="Other">Other</option>
                    </select>
                    <textarea
                      value={flagDetails}
                      onChange={e => setFlagDetails(e.target.value)}
                      placeholder="Additional details (optional)"
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-400"
                    />
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => setFlagModal(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      disabled={!flagReason || flagSubmitting}
                      onClick={async () => {
                        if (!flagReason) return;
                        setFlagSubmitting(true);
                        try {
                          const token = localStorage.getItem('token');
                          await fetch(`${SERVER_URL}/posts/${post.id}/flag`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                            body: JSON.stringify({ reason: flagReason, details: flagDetails || null }),
                          });
                          setFlagDone(true);
                        } catch (_) {
                          setFlagDone(true);
                        } finally {
                          setFlagSubmitting(false);
                        }
                      }}
                      className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-50"
                    >
                      {flagSubmitting ? 'Submitting…' : 'Submit'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Share Modal */}
        {showShareModal && (() => {
          const postUrl = `${window.location.origin}/profile/${post.user_id}#post-${post.id}`;
          const encodedUrl = encodeURIComponent(postUrl);
          const encodedTitle = encodeURIComponent(post.title || '');
          return (
            <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black bg-opacity-40" onClick={() => setShowShareModal(false)}>
              <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-sm mx-0 sm:mx-4 p-5" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">Share this post</h3>
                  <button onClick={() => setShowShareModal(false)} className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                      <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                    </svg>
                  </button>
                </div>

                {/* Post preview */}
                <div className="border border-gray-200 rounded-xl p-3 mb-5">
                  <p className="text-sm text-gray-800 line-clamp-3 mb-2">{post.title}</p>
                  <div className="flex items-center gap-2">
                    <Avatar user={{ name: post.user_name, profile_image_url: post.profile_image_url }} size="xs" />
                    <span className="text-xs font-medium text-gray-700">{post.user_name}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                    <span className="flex items-center gap-1"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#ef4444" className="w-3 h-3"><path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 0 1-.383-.218 25.18 25.18 0 0 1-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0 1 12 5.052 5.5 5.5 0 0 1 16.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 0 1-4.244 3.17 15.247 15.247 0 0 1-.383.219l-.022.012-.007.004-.003.001a.752.752 0 0 1-.704 0l-.003-.001Z" /></svg>{post.like_count || 0}</span>
                    <span className="flex items-center gap-1"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#eab308" className="w-3 h-3"><path fillRule="evenodd" d="M4.848 2.771A49.144 49.144 0 0 1 12 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 0 1-3.476.383.39.39 0 0 0-.297.17l-2.755 4.133a.75.75 0 0 1-1.248 0l-2.755-4.133a.39.39 0 0 0-.297-.17 48.9 48.9 0 0 1-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97Z" clipRule="evenodd" /></svg>{post.comment_count || 0}</span>
                  </div>
                </div>

                {/* Social platforms */}
                <div className="flex justify-around mb-5">
                  {[
                    { label: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, bg: '#1877F2', icon: (
                      <svg viewBox="0 0 24 24" fill="white" className="w-6 h-6"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    )},
                    { label: 'WhatsApp', href: `https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`, bg: '#25D366', icon: (
                      <svg viewBox="0 0 24 24" fill="white" className="w-6 h-6"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>
                    )},
                    { label: 'X', href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`, bg: '#000', icon: (
                      <svg viewBox="0 0 24 24" fill="white" className="w-5 h-5"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    )},
                  ].map(({ label, href, bg, icon }) => (
                    <a key={label} href={href} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1.5">
                      <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: bg }}>
                        {icon}
                      </div>
                      <span className="text-xs text-gray-600 font-medium">{label}</span>
                    </a>
                  ))}
                </div>

                {/* Secondary actions */}
                <div className="flex justify-around">
                  {[
                    { label: 'Copy link', icon: (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-gray-600">
                        <path d="M12.232 4.232a2.5 2.5 0 0 1 3.536 3.536l-1.225 1.224a.75.75 0 0 0 1.061 1.06l1.224-1.224a4 4 0 0 0-5.656-5.656l-3 3a4 4 0 0 0 .225 5.865.75.75 0 0 0 .977-1.138 2.5 2.5 0 0 1-.142-3.667l3-3Z" />
                        <path d="M11.603 7.963a.75.75 0 0 0-.977 1.138 2.5 2.5 0 0 1 .142 3.667l-3 3a2.5 2.5 0 0 1-3.536-3.536l1.225-1.224a.75.75 0 0 0-1.061-1.06l-1.224 1.224a4 4 0 1 0 5.656 5.656l3-3a4 4 0 0 0-.225-5.865Z" />
                      </svg>
                    ), action: () => { navigator.clipboard.writeText(postUrl); setCopiedLink(true); setTimeout(() => setCopiedLink(false), 2000); }},
                    { label: 'Email', href: `mailto:?subject=${encodedTitle}&body=${encodedUrl}`, icon: (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-gray-600">
                        <path d="M3 4a2 2 0 0 0-2 2v1.161l8.441 4.221a1.25 1.25 0 0 0 1.118 0L19 7.161V6a2 2 0 0 0-2-2H3Z" />
                        <path d="m19 8.839-7.77 3.885a2.75 2.75 0 0 1-2.46 0L1 8.839V14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8.839Z" />
                      </svg>
                    )},
                  ].map(({ label, icon, action, href }) => (
                    href ? (
                      <a key={label} href={href} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1.5">
                        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">{icon}</div>
                        <span className="text-xs text-gray-600 font-medium">{label}</span>
                      </a>
                    ) : (
                      <button key={label} onClick={action} className="flex flex-col items-center gap-1.5">
                        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">{icon}</div>
                        <span className="text-xs text-gray-600 font-medium">{copiedLink && label === 'Copy link' ? 'Copied!' : label}</span>
                      </button>
                    )
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Pool Display */}
        <PoolDisplay post={post} onProgressBarClick={() => onToggleDonors(post.id)} />

        {/* Goal Met Banner - show when goal is reached */}
        {post.goal_amount && post.current_amount >= post.goal_amount && (
          <div className="mb-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3 text-center">
            <p className="text-sm font-semibold text-green-800">
              🎉 Goal reached! Any further gifts provide an extra cushion.
            </p>
          </div>
        )}

        {/* Beneficiary info banner - show to beneficiary when there's money deposited */}
        {isProfilePage &&
          isAuthenticated &&
          user &&
          post.beneficiary_email === user.email &&
          post.current_amount > 0 &&
          (post.acceptance_status === 'accepted' || !post.acceptance_status) && (
            <BeneficiaryInfoBanner
              amount={post.current_amount}
              userId={user.id}
              colorScheme="green"
            />
          )}


        {/* Pending acceptance message */}
        {post.acceptance_status === 'pending' && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-center">
            <p className="text-sm text-indigo-800 font-medium">
              ⏳ Waiting for {post.beneficiary_name} to accept this gift pool
            </p>
          </div>
        )}

        {/* Declined message */}
        {post.acceptance_status === 'declined' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <p className="text-sm text-red-800 font-medium mb-1">
              ❌ {post.beneficiary_name} has declined this gift pool
            </p>
            {post.declined_reason && (
              <p className="text-xs text-red-700 italic mb-2">Reason: {post.declined_reason}</p>
            )}
            <p className="text-xs text-red-600 mt-2">
              🔒 This post is only visible to you and {post.beneficiary_name}
            </p>
          </div>
        )}
      </div>

      {/* Gifters Section */}
      <EngagementSection
        expanded={expandedDonors.has(post.id)}
        onClose={() => onToggleDonors(post.id)}
        title={<>💝 Gifts ({post.gift_count || 0})</>}
        items={donors[post.id] ? [...donors[post.id]].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) : []}
        emptyMessage="No gifters yet. Be the first!"
        bg="bg-indigo-50"
        renderItem={(donor, idx) => (
          <Link key={idx} to={`/profile/${donor.giver_id}`} className="flex justify-between items-center bg-white p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
            <div className="flex items-center gap-3">
              <Avatar name={donor.giver_name} email={donor.giver_email} profilePhotoUrl={donor.giver_profile_photo_url} size="w-8 h-8" textSize="text-sm" />
              <div className="flex flex-col">
                <span className="font-medium text-gray-900 hover:text-indigo-600 transition-colors">{donor.giver_name}</span>
                <span className="text-xs text-gray-500">{formatDateTime(donor.created_at)}</span>
              </div>
            </div>
            <span className="text-lg font-bold text-indigo-600">${donor.amount.toFixed(2)}</span>
          </Link>
        )}
      />

      {/* Likes Section */}
      <EngagementSection
        expanded={expandedLikes.has(post.id)}
        onClose={() => onToggleLikes(post.id)}
        title={<><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#ef4444" className="w-4 h-4"><path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 0 1-.383-.218 25.18 25.18 0 0 1-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0 1 12 5.052 5.5 5.5 0 0 1 16.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 0 1-4.244 3.17 15.247 15.247 0 0 1-.383.219l-.022.012-.007.004-.003.001a.752.752 0 0 1-.704 0l-.003-.001Z" /></svg>Loves ({post.like_count || 0})</>}
        items={likers[post.id] || []}
        emptyMessage="No loves yet. Be the first!"
        renderItem={(liker, idx) => {
          const inner = (
            <div className="flex justify-between items-center bg-white p-3 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3">
                <Avatar name={liker.user_name} email={liker.user_email} profilePhotoUrl={liker.user_profile_photo_url} size="w-8 h-8" textSize="text-sm" />
                <span className="font-medium text-gray-900 hover:text-gray-600 transition-colors">{liker.user_name}</span>
              </div>
              <span className="text-xs text-gray-500">{formatDateTime(liker.created_at)}</span>
            </div>
          );
          return liker.user_id
            ? <Link key={idx} to={`/profile/${liker.user_id}`}>{inner}</Link>
            : <div key={idx}>{inner}</div>;
        }}
      />

      {/* Sharers Section */}
      <EngagementSection
        expanded={!!(expandedShares && expandedShares.has(post.id))}
        onClose={() => onToggleShares && onToggleShares(post.id)}
        title={<><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#22c55e" className="w-4 h-4"><path fillRule="evenodd" d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0V5.36l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389A5.5 5.5 0 0 1 13.89 6.11l.311.31h-2.432a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.219Z" clipRule="evenodd" /></svg>Reposts ({post.share_count || 0})</>}
        items={(sharers && sharers[post.id]) || []}
        emptyMessage="No reposts yet."
        renderItem={(sharer, idx) => {
          const inner = (
            <div className="flex justify-between items-center bg-white p-3 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3">
                <Avatar name={sharer.user_name} email={sharer.user_email} profilePhotoUrl={sharer.user_profile_photo_url} size="w-8 h-8" textSize="text-sm" />
                <span className="font-medium text-gray-900 hover:text-gray-600 transition-colors">{sharer.user_name}</span>
              </div>
              <span className="text-xs text-gray-500">{formatDateTime(sharer.created_at)}</span>
            </div>
          );
          return sharer.user_id
            ? <Link key={idx} to={`/profile/${sharer.user_id}`}>{inner}</Link>
            : <div key={idx}>{inner}</div>;
        }}
      />

      {/* Vouchers Section */}
      <EngagementSection
        expanded={!!(expandedVouches && expandedVouches.has(post.id))}
        onClose={() => onToggleVouches && onToggleVouches(post.id)}
        title={<><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#3b82f6" className="w-4 h-4"><path fillRule="evenodd" d="M12.516 2.17a.75.75 0 0 0-1.032 0 11.209 11.209 0 0 1-7.877 3.08.75.75 0 0 0-.722.515A12.74 12.74 0 0 0 2.25 9.75c0 5.942 4.064 10.933 9.563 12.348a.749.749 0 0 0 .374 0c5.499-1.415 9.563-6.406 9.563-12.348 0-1.39-.223-2.73-.635-3.985a.75.75 0 0 0-.722-.516l-.143.001c-2.996 0-5.717-1.17-7.734-3.08Zm3.094 8.016a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" /></svg>Vouchers ({post.vouch_count || 0})</>}
        items={(vouchers && vouchers[post.id]) || []}
        emptyMessage="No vouches yet. Be the first!"
        renderItem={(voucher, idx) => (
          <Link key={idx} to={`/profile/${voucher.user_id}`} className="flex justify-between items-center bg-white p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
            <div className="flex items-center gap-3">
              <Avatar name={voucher.user_name} email={voucher.user_email} profilePhotoUrl={voucher.user_profile_photo_url} size="w-8 h-8" textSize="text-sm" />
              <span className="font-medium text-gray-900 hover:text-gray-600 transition-colors">{voucher.user_name}</span>
            </div>
            <span className="text-xs text-gray-500">{formatDateTime(voucher.created_at)}</span>
          </Link>
        )}
      />

      {/* Comments Section */}
      {expandedComments.has(post.id) && (
        <div className="px-3 sm:px-5 py-4 border-t border-gray-100 bg-gray-50">
          {/* Comment Input */}
          <div className="mb-4">
            <div className="flex gap-2">
              <textarea
                placeholder="Add a comment..."
                value={newComment[post.id] || ''}
                onChange={e => onCommentChange(post.id, e.target.value)}
                rows={1}
                onInput={e => {
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              />
              <button
                onClick={() => onAddComment(post.id)}
                className="self-end px-6 py-2 bg-indigo-500 text-white rounded-full font-medium hover:bg-indigo-600 transition-colors"
              >
                Post
              </button>
            </div>
          </div>

          {/* Comments List */}
          <div className="space-y-3">
            {comments[post.id] && comments[post.id].length > 0 ? (
              comments[post.id].map(comment => (
                <div key={comment.id} className="border-b border-gray-200 last:border-0 pb-3 last:pb-0">
                  {/* Top-level comment */}
                  <div className="flex gap-3">
                    <div className="flex flex-col items-center flex-shrink-0">
                      <Link to={comment.user_id ? `/profile/${comment.user_id}` : '#'} className="flex-shrink-0">
                        <Avatar name={comment.user_name} email={comment.user_email} profilePhotoUrl={comment.user_profile_photo_url} size="w-8 h-8" textSize="text-sm" />
                      </Link>
                      {((comment.replies && comment.replies.length > 0) || (replyingTo && replyingTo[post.id] === comment.id)) && (
                        <div className="w-0.5 bg-gray-200 flex-1 mt-1" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Link to={comment.user_id ? `/profile/${comment.user_id}` : '#'} className="font-semibold text-sm text-gray-900 hover:underline">
                          {comment.user_name || 'Anonymous'}
                        </Link>
                        <span className="text-xs text-gray-500">
                          {formatDateTime(comment.created_at)}
                        </span>
                      </div>
                      {editingCommentId === comment.id ? (
                        <div className="mt-1">
                          <textarea
                            value={editingCommentText}
                            onChange={e => setEditingCommentText(e.target.value)}
                            rows={2}
                            onInput={e => {
                              e.target.style.height = 'auto';
                              e.target.style.height = e.target.scrollHeight + 'px';
                            }}
                            autoFocus
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                          />
                          <div className="flex gap-2 mt-1">
                            <button
                              onClick={() => {
                                onEditComment && onEditComment(post.id, comment.id, editingCommentText);
                                setEditingCommentId(null);
                              }}
                              className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingCommentId(null)}
                              className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-700 whitespace-pre-line">
                          {comment.is_admin ? renderAdminCommentContent(comment.content) : comment.content}
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-1">
                        <button
                          onClick={() => onReplyClick && onReplyClick(post.id, comment.id, comment.user_name)}
                          className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                        >
                          Reply
                        </button>
                        {isAuthenticated && (
                          <button
                            onClick={() => handleCommentLike(comment.id)}
                            className="flex items-center gap-1 text-xs font-medium transition-colors"
                          >
                            <span className={commentLikes[comment.id]?.liked ? 'text-red-500' : 'text-gray-400 hover:text-red-400'}>
                              {commentLikes[comment.id]?.liked ? '♥' : '♡'}
                            </span>
                            {(commentLikes[comment.id]?.count || 0) > 0 && (
                              <span className="text-gray-500">{commentLikes[comment.id].count}</span>
                            )}
                          </button>
                        )}
                        {user && comment.user_email === user.email && editingCommentId !== comment.id && (
                          <>
                            <button
                              onClick={() => {
                                setEditingCommentId(comment.id);
                                setEditingCommentText(comment.content);
                              }}
                              className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => onDeleteComment && onDeleteComment(post.id, comment.id)}
                              className="text-xs text-gray-500 hover:text-red-600 font-medium"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Replies */}
                  {comment.replies && comment.replies.length > 0 && (
                    <div className="ml-11 mt-0.5 space-y-2">
                      {comment.replies.map(reply => (
                        <div key={reply.id} className="flex gap-2">
                          <Link to={reply.user_id ? `/profile/${reply.user_id}` : '#'} className="flex-shrink-0">
                            <Avatar name={reply.user_name} email={reply.user_email} profilePhotoUrl={reply.user_profile_photo_url} size="w-6 h-6" textSize="text-xs" />
                          </Link>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-0.5">
                              <Link to={reply.user_id ? `/profile/${reply.user_id}` : '#'} className="font-semibold text-xs text-gray-900 hover:underline">
                                {reply.user_name || 'Anonymous'}
                              </Link>
                              <span className="text-xs text-gray-500">
                                {formatDateTime(reply.created_at)}
                              </span>
                            </div>
                            {editingCommentId === reply.id ? (
                              <div className="mt-1">
                                <textarea
                                  value={editingCommentText}
                                  onChange={e => setEditingCommentText(e.target.value)}
                                  rows={1}
                                  onInput={e => {
                                    e.target.style.height = 'auto';
                                    e.target.style.height = e.target.scrollHeight + 'px';
                                  }}
                                  autoFocus
                                  className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                                />
                                <div className="flex gap-2 mt-1">
                                  <button
                                    onClick={() => {
                                      onEditComment && onEditComment(post.id, reply.id, editingCommentText);
                                      setEditingCommentId(null);
                                    }}
                                    className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => setEditingCommentId(null)}
                                    className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-xs text-gray-700 whitespace-pre-line">
                                {reply.is_admin ? renderAdminCommentContent(reply.content) : reply.content}
                              </p>
                            )}
                            <div className="flex items-center gap-3 mt-0.5">
                              {isAuthenticated && (
                                <button
                                  onClick={() => handleCommentLike(reply.id)}
                                  className="flex items-center gap-1 text-xs font-medium transition-colors"
                                >
                                  <span className={commentLikes[reply.id]?.liked ? 'text-red-500' : 'text-gray-400 hover:text-red-400'}>
                                    {commentLikes[reply.id]?.liked ? '♥' : '♡'}
                                  </span>
                                  {(commentLikes[reply.id]?.count || 0) > 0 && (
                                    <span className="text-gray-500">{commentLikes[reply.id].count}</span>
                                  )}
                                </button>
                              )}
                              {user && reply.user_email === user.email && editingCommentId !== reply.id && (
                                <>
                                  <button
                                    onClick={() => {
                                      setEditingCommentId(reply.id);
                                      setEditingCommentText(reply.content);
                                    }}
                                    className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => onDeleteComment && onDeleteComment(post.id, reply.id)}
                                    className="text-xs text-gray-500 hover:text-red-600 font-medium"
                                  >
                                    Delete
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Reply input */}
                  {replyingTo && replyingTo[post.id] === comment.id && (
                    <div className="ml-11 mt-1">
                      <div className="flex gap-2">
                        <textarea
                          placeholder={`Reply...`}
                          value={(replyText && replyText[post.id]) || ''}
                          onChange={e => onReplyTextChange && onReplyTextChange(post.id, e.target.value)}
                          rows={1}
                          onInput={e => {
                            e.target.style.height = 'auto';
                            e.target.style.height = e.target.scrollHeight + 'px';
                          }}
                          autoFocus
                          onFocus={e => { const len = e.target.value.length; e.target.setSelectionRange(len, len); }}
                          className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                        />
                        <button
                          onClick={() => onAddReply && onAddReply(post.id, comment.id)}
                          className="px-4 py-1.5 text-sm bg-indigo-500 text-white rounded-full font-medium hover:bg-indigo-600 transition-colors"
                        >
                          Reply
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                No comments yet. Be the first to comment!
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default GiftPoolPost;
